import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Searchbar } from "../../common/components/general/Searchbar";
import {
    ProductFilterSkeleton,
    ProductFilterWrapper,
} from "./components/ProductFilterWrapper";
import { Products } from "./components/Products";
import {
    resetFilter,
    setGridLayout,
    setMobileFiltersActive,
} from "./ProductRangeSlice";
import { useGetProductFilterOptionsQuery } from "../../common/api/product";
import { SvgReset } from "../../common/components/svg/SvgReset";
import { ProductTypeEnum } from "../../common/enums/ProductTypeEnum";
import { hasSelectedFilters } from "../../common/utils/productRange/buildProductRangeOptionsQuery";
import { JSX } from "react";
import { useStringTranslation } from "../../common/hooks/useStringTranslation";

const ProductRangePageContainer = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const { t, isLoading: isLoadingTranslations } = useStringTranslation();

    const { gridLayout, selectedFilters, productType, mobileFiltersActive } =
        useAppSelector((state) => state.productRange);

    const { data: filtersResponse, isLoading: isLoadingFilters } =
        useGetProductFilterOptionsQuery();
    const filters = filtersResponse?.data;

    const hasFilters = hasSelectedFilters(selectedFilters);

    return (
        <>
            <div className="p-product-range">
                <div className="p-product-range__header">
                    <div className="c-breadcrumbs">
                        {isLoadingTranslations ? (
                            <>
                                <span className="skeleton skeleton-name"></span>
                                <div className="c-breadcrumbs__arrow">
                                    <img
                                        src="/images/arrow-black.svg"
                                        alt="arrow"
                                    />
                                </div>
                                <span className="skeleton skeleton-name"></span>
                            </>
                        ) : (
                            <>
                                <span>{t("product-range")}</span>
                                <div className="c-breadcrumbs__arrow">
                                    <img
                                        src="/images/arrow-black.svg"
                                        alt="arrow"
                                    />
                                </div>
                                {productType === ProductTypeEnum.products && (
                                    <span>{t("machines")}</span>
                                )}
                                {productType ===
                                    ProductTypeEnum.accessories && (
                                    <span>{t("accessories")}</span>
                                )}
                            </>
                        )}
                    </div>
                    <Searchbar />
                </div>

                <div className="p-product-range__content">
                    {isLoadingFilters ? (
                        <div className="c-product-filters">
                            <div className="c-product-filters__container">
                                {Array(5)
                                    .fill(0)
                                    .map((_, index) => (
                                        <ProductFilterSkeleton key={index} />
                                    ))}
                            </div>
                        </div>
                    ) : (
                        <div
                            className={
                                "c-product-filters " +
                                (mobileFiltersActive ? "is-active" : "")
                            }
                        >
                            <div className="c-product-filters__container">
                                {!!filters &&
                                    filters.map((filter, index) => (
                                        <ProductFilterWrapper
                                            key={index}
                                            filter={filter}
                                        />
                                    ))}
                                <div className="c-product-filters__btn-reset__container">
                                    <button
                                        className={`c-product-filters__btn-reset ${hasFilters && "c-product-filters__btn-reset--active"}`}
                                        onClick={() => dispatch(resetFilter())}
                                    >
                                        <SvgReset
                                            color={
                                                hasFilters ? "white" : "black"
                                            }
                                        />
                                        <span>{t("reset-filters")}</span>
                                    </button>
                                </div>
                            </div>
                            <div
                                className="c-product-filters__mobile-footer"
                                onClick={() =>
                                    dispatch(
                                        setMobileFiltersActive(
                                            !mobileFiltersActive,
                                        ),
                                    )
                                }
                            >
                                <img src="/images/filter.svg" alt="" />
                                <span>{t("filter-results")}</span>
                            </div>
                        </div>
                    )}

                    <div className="p-product-range__content__range">
                        <div className="p-product-range__content__range__mobile-nav">
                            <div
                                className="c-product-filter-menu-btn"
                                onClick={() =>
                                    dispatch(
                                        setMobileFiltersActive(
                                            !mobileFiltersActive,
                                        ),
                                    )
                                }
                            >
                                <img src="/images/filter.svg" alt="" />
                                {isLoadingTranslations ? (
                                    <span className="skeleton skeleton-button"></span>
                                ) : (
                                    <span>{t("filter-results")}</span>
                                )}
                            </div>
                            <div className="c-products-layout-switch">
                                <div
                                    className={
                                        "c-products-layout-switch__button " +
                                        (!gridLayout ? "is-active" : "")
                                    }
                                    onClick={() =>
                                        dispatch(setGridLayout(false))
                                    }
                                >
                                    {gridLayout && (
                                        <img
                                            src="/images/list-view-black.svg"
                                            alt="list"
                                        />
                                    )}
                                    {!gridLayout && (
                                        <img
                                            src="/images/list-view-white.svg"
                                            alt="list"
                                        />
                                    )}

                                    {!isLoadingTranslations ? (
                                        <span>{t("list")}</span>
                                    ) : (
                                        ""
                                    )}
                                </div>
                                <div
                                    className={
                                        "c-products-layout-switch__button " +
                                        (gridLayout ? "is-active" : "")
                                    }
                                    onClick={() =>
                                        dispatch(setGridLayout(true))
                                    }
                                >
                                    {!gridLayout && (
                                        <img
                                            src="/images/grid-view-black.svg"
                                            alt="grid"
                                        />
                                    )}
                                    {gridLayout && (
                                        <img
                                            src="/images/grid-view-white.svg"
                                            alt="grid"
                                        />
                                    )}

                                    {!isLoadingTranslations ? (
                                        <span>{t("grid")}</span>
                                    ) : (
                                        ""
                                    )}
                                </div>
                            </div>
                        </div>
                        <Products />
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProductRangePageContainer;
